import React from 'react';
import { Timer } from 'lucide-react';

import { TopBar, Pill, IconButton } from './';

/**
 * Unified top bar for the in-game phases (Spin / Race / Tally / Leaderboard /
 * Final). Always shows:
 *   - left:   "ROUND N / M" pill (the only round indicator on screen)
 *   - center: timer chip when relevant (Race only); secondary label otherwise
 *   - right:  X close button → triggers leave-game flow
 *
 * Per `.nookli/designs/screens-2-gameplay.jsx`: every gameplay screen uses
 * this same chrome so the player always knows where they are. We don't show
 * "TALLY · 1/4" or "R 3" in two places — the round comes from the left
 * pill, the secondary state goes in `centerLabel`.
 */
export interface GameTopBarProps {
  round: number;
  /**
   * How many rounds this game has. Pass `0` when there is no such number — a
   * LADDER RUN has no length: it lasts exactly as long as you keep beating Atom,
   * and rendering "ROUND 3 / 26" would be telling the player they have 23 rounds
   * left when in fact they have one life. The pill drops the denominator instead.
   */
  totalRounds: number;
  /** Optional secondary label shown center (e.g. "TALLY · 2/4"). */
  centerLabel?: React.ReactNode;
  /** Time remaining in seconds — when set, renders a timer chip in center
      and the centerLabel is suppressed. */
  timeRemaining?: number | null;
  /** When timeRemaining is set, becomes urgent (coral pulse) at ≤10s. */
  urgentAt?: number;
  onClose?: () => void;
}

export function GameTopBar({
  round,
  totalRounds,
  centerLabel,
  timeRemaining,
  urgentAt = 10,
  onClose,
}: GameTopBarProps) {
  let center: React.ReactNode = centerLabel || null;
  if (typeof timeRemaining === 'number' && timeRemaining > 0) {
    const minutes = Math.floor(timeRemaining / 60);
    const sec = (timeRemaining % 60).toString().padStart(2, '0');
    const urgent = timeRemaining <= urgentAt;
    center = (
      <span className={`timer-ring ${urgent ? 'urgent' : ''}`}>
        <Timer size={14} color={urgent ? 'white' : undefined} /> {minutes}:{sec}
      </span>
    );
  }

  return (
    <TopBar
      left={
        <Pill icon="Repeat2">
          ROUND {round}{totalRounds > 0 ? ` / ${totalRounds}` : ''}
        </Pill>
      }
      center={center}
      right={
        onClose ? (
          <IconButton icon="X" ariaLabel="Leave game" onClick={onClose} />
        ) : null
      }
    />
  );
}
